import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Phone, MessageCircle, MapPin, Wrench, Shield, Users, Package, Car, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    branch: '',
    requirement: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const branches = [
    {
      name: 'Vepery Branch',
      address: 'Ganga Motors, New No. 139, Old No. 43, Vepery High Road, Periamet, Periyamedu, Poongavanapuram, Chennai – 600003, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Vepery+Chennai'
    },
    {
      name: 'Maduravoyal Branch',
      address: 'Ganga Motors, No. 203, Sathiyamoorthy Nagar, Maduravoyal, Chennai – 600095, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Maduravoyal+Chennai'
    },
    {
      name: 'Red Hills Branch',
      address: 'Ganga Motors, No. 468, Jeyachandra Nagar, Red Hills, Vedakarai, Chennai – 600052, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Red+Hills+Chennai'
    },
    {
      name: 'Gummidipoondi Branch',
      address: 'Ganga Motors, No. 138, GNT Road, Gummidipoondi – 601201, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Gummidipoondi'
    },
    {
      name: 'Tiruvallur Branch',
      address: 'Ganga Motors, M.S. Complex, JN Road, Tiruvallur – 602001, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Tiruvallur'
    },
    {
      name: 'Thiruttani Branch',
      address: 'Ganga Motors, No. 106, Senthamil Nagar, Thiruttani – 631209, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Thiruttani'
    },
    {
      name: 'Virudhachalam Branch',
      address: 'Ganga Motors, No. 35/9B1A, Ponneri Bypass, Virudhachalam, Cuddalore District – 606003, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Virudhachalam'
    },
    {
      name: 'Cuddalore Branch',
      address: 'Ganga Motors, No. 4, 5 & 6, Siva Complex, Block 1, Imperial Road, Chellankuppam, Cuddalore – 607003, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Cuddalore'
    },
    {
      name: 'Cuddalore Periyagangnakuppam Branch',
      address: 'Ganga Motors, No. 43/6 Cuddalore Pondy Main Road, Periyagangnakuppam, Next RK ITI, Cuddalore – 607006, Tamil Nadu',
      phone: '9941499412',
      mapLink: 'https://maps.google.com/?q=Ganga+Motors+Cuddalore+Periyagangnakuppam'
    }
  ];

  const highlights = [
    { icon: MapPin, title: 'Wide Branch Network', description: '9 branches across Chennai, Tiruvallur & Cuddalore' },
    { icon: Package, title: 'Genuine Spare Parts', description: 'Original Piaggio parts for all models' },
    { icon: Wrench, title: 'Trained Service Team', description: 'Expert technicians for quality service' },
    { icon: Shield, title: 'Easy Finance & Insurance', description: 'Quick approval & comprehensive coverage' }
  ];

  const vehicleCategories = [
    {
      title: 'Passenger 3-Wheelers',
      description: 'Comfortable and reliable auto-rickshaws for passenger transport',
      icon: Users
    },
    {
      title: 'Cargo 3-Wheelers',
      description: 'Robust commercial vehicles for goods transportation',
      icon: Package
    },
    {
      title: 'Service & Spares',
      description: 'Complete service solutions and genuine spare parts',
      icon: Wrench
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Auto Rickshaw Owner',
      content: 'Excellent service and genuine spare parts. The team is very professional and helpful.'
    },
    {
      name: 'Anitha Devi',
      role: 'Transport Business Owner',
      content: 'Best place for Piaggio vehicles. Fast service and good finance options made purchasing easy.'
    },
    {
      name: 'Murugan S',
      role: 'Cargo Business',
      content: 'Reliable vehicles and excellent after-sales support. Highly recommend Ganga Motors!'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (!formData.branch) newErrors.branch = 'Please select a branch';
    if (!formData.requirement) newErrors.requirement = 'Please select a requirement';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ name: '', mobile: '', branch: '', requirement: '', message: '' });
      }, 5000);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Floating Action Buttons */}
      <div className="floating-buttons">
        <a href="tel:+919941499412" className="floating-btn call-btn" aria-label="Call Now">
          <Phone className="h-6 w-6" />
        </a>
        <a href="https://wa.me/917806804737" target="_blank" rel="noopener noreferrer" className="floating-btn whatsapp-btn" aria-label="WhatsApp">
          <MessageCircle className="h-6 w-6" />
        </a>
      </div>

      {/* Header */}
      <header className="header-sticky">
        <div className="header-container">
          <div className="logo">
            <img src="/logo.jpg" alt="Ganga Motors" className="logo-image" />
          </div>
          <nav className="nav-menu">
            <button onClick={() => scrollToSection('home')} className="nav-link">Home</button>
            <button onClick={() => scrollToSection('vehicles')} className="nav-link">Vehicles</button>
            <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
            <button onClick={() => scrollToSection('branches')} className="nav-link">Branches</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </nav>
          <div className="header-ctas">
            <a href="tel:+919941499412">
              <Button variant="outline" size="sm" className="header-btn">
                <Phone className="h-4 w-4 mr-2" />
                Call Now
              </Button>
            </a>
            <a href="https://wa.me/917806804737" target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="header-btn-primary">
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h2 className="hero-title">Piaggio Commercial Vehicles for Passenger & Cargo Needs</h2>
            <p className="hero-subtitle">Sales • Service • Spare Parts • Finance & Insurance Support</p>
            <div className="hero-ctas">
              <Button size="lg" className="cta-primary" onClick={() => scrollToSection('contact')}>
                Get a Quote
              </Button>
              <Button size="lg" variant="outline" className="cta-secondary" onClick={() => scrollToSection('contact')}>
                Book a Test Drive
              </Button>
            </div>
          </div>
          <div className="hero-image">
            <div className="image-placeholder">
              <Car className="placeholder-icon" />
              <p>Piaggio 3-Wheeler</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="highlights-section">
        <div className="section-container">
          <div className="highlights-grid">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <Card key={index} className="highlight-card">
                  <div className="highlight-icon">
                    <Icon className="h-8 w-8" />
                  </div>
                  <h3 className="highlight-title">{highlight.title}</h3>
                  <p className="highlight-description">{highlight.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vehicle Categories Section */}
      <section id="vehicles" className="vehicles-section">
        <div className="section-container">
          <h2 className="section-title">Our Vehicle Range</h2>
          <div className="vehicles-grid">
            {vehicleCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="vehicle-card">
                  <div className="vehicle-icon">
                    <Icon className="h-12 w-12" />
                  </div>
                  <h3 className="vehicle-title">{category.title}</h3>
                  <p className="vehicle-description">{category.description}</p>
                  <Button className="vehicle-btn" onClick={() => scrollToSection('contact')}>
                    Enquire Now
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section id="contact" className="form-section">
        <div className="section-container">
          <div className="form-wrapper">
            <h2 className="section-title">Request a Callback</h2>
            <p className="form-subtitle">Fill in your details and our team will get back to you shortly</p>
            
            {showSuccess ? (
              <div className="success-message">
                <CheckCircle className="success-icon" />
                <h3>Thanks! Our team will call you shortly.</h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="enquiry-form">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    className={errors.name ? 'input-error' : ''}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="mobile">Mobile Number *</label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="Enter 10-digit mobile number"
                    className={errors.mobile ? 'input-error' : ''}
                  />
                  {errors.mobile && <span className="error-text">{errors.mobile}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="branch">Select Branch *</label>
                  <Select value={formData.branch} onValueChange={(value) => setFormData({ ...formData, branch: value })}>
                    <SelectTrigger className={errors.branch ? 'input-error' : ''}>
                      <SelectValue placeholder="Choose your nearest branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vepery">Vepery – Chennai</SelectItem>
                      <SelectItem value="maduravoyal">Maduravoyal – Chennai</SelectItem>
                      <SelectItem value="redhills">Red Hills – Chennai</SelectItem>
                      <SelectItem value="gummidipoondi">Gummidipoondi</SelectItem>
                      <SelectItem value="tiruvallur">Tiruvallur</SelectItem>
                      <SelectItem value="thiruttani">Thiruttani</SelectItem>
                      <SelectItem value="virudhachalam">Virudhachalam</SelectItem>
                      <SelectItem value="cuddalore">Cuddalore – Chellankuppam</SelectItem>
                      <SelectItem value="cuddalore2">Cuddalore – Periyagangnakuppam</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.branch && <span className="error-text">{errors.branch}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="requirement">Requirement *</label>
                  <Select value={formData.requirement} onValueChange={(value) => setFormData({ ...formData, requirement: value })}>
                    <SelectTrigger className={errors.requirement ? 'input-error' : ''}>
                      <SelectValue placeholder="What are you looking for?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="passenger">Passenger Vehicle</SelectItem>
                      <SelectItem value="cargo">Cargo Vehicle</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="spareparts">Spare Parts</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.requirement && <span className="error-text">{errors.requirement}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message (Optional)</label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Any specific requirements or questions?"
                    rows={4}
                  />
                </div>

                <Button type="submit" size="lg" className="submit-btn">
                  Request Callback
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section id="branches" className="branches-section">
        <div className="section-container">
          <h2 className="section-title">Our Branches</h2>
          <div className="branches-grid">
            {branches.map((branch, index) => (
              <Card key={index} className="branch-card">
                <div className="branch-header">
                  <MapPin className="branch-icon" />
                  <h3 className="branch-name">{branch.name}</h3>
                </div>
                <p className="branch-address">{branch.address}</p>
                <div className="branch-contact">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:+91${branch.phone}`} className="branch-phone">+91 {branch.phone}</a>
                </div>
                <a href={branch.mapLink} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="map-btn">
                    Open in Google Maps
                  </Button>
                </a>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <h2 className="section-title">Our Services</h2>
          <div className="services-grid">
            <div className="service-item">
              <Car className="service-icon" />
              <h3>Sales Consultation</h3>
              <p>Expert guidance to choose the right vehicle for your business</p>
            </div>
            <div className="service-item">
              <Wrench className="service-icon" />
              <h3>Service Booking</h3>
              <p>Professional maintenance and repair services</p>
            </div>
            <div className="service-item">
              <Package className="service-icon" />
              <h3>Spare Parts</h3>
              <p>Genuine Piaggio spare parts always in stock</p>
            </div>
            <div className="service-item">
              <Shield className="service-icon" />
              <h3>Finance Support</h3>
              <p>Easy financing options with quick approval</p>
            </div>
            <div className="service-item">
              <Shield className="service-icon" />
              <h3>Insurance Support</h3>
              <p>Comprehensive insurance coverage assistance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-container">
          <h2 className="section-title">What Our Customers Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <p className="author-name">{testimonial.name}</p>
                  <p className="author-role">{testimonial.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <img src="/logo.jpg" alt="Ganga Motors" className="footer-logo" />
            <p>Authorized Piaggio Commercial Vehicle Dealership</p>
          </div>
          <div className="footer-section">
            <h4>Contact Us</h4>
            <p><Phone className="inline h-4 w-4 mr-2" />+91 99414 99412</p>
            <p><Phone className="inline h-4 w-4 mr-2" />+91 7806 804737</p>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <button onClick={() => scrollToSection('home')} className="footer-link">Home</button>
            <button onClick={() => scrollToSection('vehicles')} className="footer-link">Vehicles</button>
            <button onClick={() => scrollToSection('services')} className="footer-link">Services</button>
            <button onClick={() => scrollToSection('branches')} className="footer-link">Branches</button>
          </div>
          <div className="footer-section">
            <h4>Working Hours</h4>
            <p>Monday - Saturday: 9:00 AM - 7:00 PM</p>
            <p>Sunday: 10:00 AM - 5:00 PM</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 Ganga Motors. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;